import React, { useCallback, useRef, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/Table';
import Spinner from '@/components/common/Spinner';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { Button, LinkButton } from '@/components/ui/Buttons';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import AddCorrelationPanel from '../AddCorrelationPanel';
import BulkActionBar from './BulkActionBar';
import BulkRemoveModal from './BulkRemoveModal';
import SelectionAnnouncer from '../SelectionAnnouncer';
import { COLUMN_COUNT } from './columns';
import CorrelationRow from './CorrelationRow';
import TagManagerTableFooter from './TagManagerTableFooter';
import TagManagerTableHeader from './TagManagerTableHeader';
import EmptyState from '../EmptyState';
import FilterBar from '../FilterBar';
import useCorrelationActions, {
  bulkRemoveMutationKey, bulkRestoreMutationKey, correlationMutationKey,
} from '../hooks/useCorrelationActions';
import useCorrelationFilters from '../hooks/useCorrelationFilters';
import useCorrelationKeyboard from '../hooks/useCorrelationKeyboard';
import useCorrelationSelection from '../hooks/useCorrelationSelection';
import useRowFocus from '../hooks/useRowFocus';
import { CorrelationCategory, ModelCorrelation, TagManagerVariant } from '../types';

// Also the guard on DOM size: a lesson can carry 7.5k correlations.
const ROW_CAP = 8;

const MAX_HEIGHT: Record<TagManagerVariant, string> = {
  card: 'tw-max-h-[310px]',
  inline: 'tw-max-h-[470px]',
  panel: 'tw-max-h-[45vh]',
};

interface TagManagerTableProps {
  categories: CorrelationCategory[];
  correlations: ModelCorrelation[];
  error: boolean;
  isLoading: boolean;
  modelId: number;
  modelType: string;
  /** The program whose counts a mutation should refresh. Absent on surfaces with no program. */
  programId?: number;
  variant: TagManagerVariant;
}

const TagManagerTable = ({
  categories, correlations, error, isLoading, modelId, modelType, programId, variant,
}: TagManagerTableProps) => {
  const [showAll, setShowAll] = useState(false);
  const [isConfirmingBulk, setIsConfirmingBulk] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchId = `tag-manager-${modelType}-${modelId}-search`;
  const addPanelId = `tag-manager-${modelType}-${modelId}-add`;

  const {
    categoryCount, categoryOptions, clearFilters, filtered, filters, gradeOptions, hiddenCount, isFiltered,
    setFilter, showGrade, showState, stateOptions,
  } = useCorrelationFilters({ categories, correlations });

  const visible = showAll ? filtered : filtered.slice(0, ROW_CAP);
  const remaining = filtered.length - visible.length;
  const selectable = visible.filter(correlation => !correlation.hidden);

  const {
    allSelected, clearSelection, dropIds, selectAllRef, selectedIds, selectedVisible,
    toggleSelect, toggleSelectAll,
  } = useCorrelationSelection({ filters, selectable });

  const {
    addButtonRef, focusAnchor, onBulkFailure, onFailure, onUndo,
    recordAnchorIntent, recordRowIntent, rootRef,
  } = useRowFocus({ correlations, selectAllRef });

  const { removeCorrelation, removeCorrelations, restoreCorrelation } = useCorrelationActions({
    modelId, modelType, onBulkFailure, onBulkUndo: recordAnchorIntent, onFailure, onUndo, programId,
  });

  const isRemovingBulk = useIsMutating({ mutationKey: bulkRemoveMutationKey(modelType, modelId) }) > 0;
  const isRestoringBulk = useIsMutating({ mutationKey: bulkRestoreMutationKey(modelType, modelId) }) > 0;
  const isRowMutating = useIsMutating({ mutationKey: correlationMutationKey(modelType, modelId) }) > 0;

  const onKeyDown = useCorrelationKeyboard();

  const removeCorrelationAction = useCallback((correlation: ModelCorrelation, slot: number) => {
    recordRowIntent(slot);
    removeCorrelation(correlation);
  }, [recordRowIntent, removeCorrelation]);

  const unHideCorrelationAction = useCallback((correlation: ModelCorrelation, slot: number) => {
    recordRowIntent(slot);
    restoreCorrelation(correlation);
  }, [recordRowIntent, restoreCorrelation]);

  const confirmBulkRemove = () => {
    recordAnchorIntent();

    removeCorrelations(selectedVisible).then((removedIds) => {
      // Subtract rather than reset: the modal may have been dismissed mid-flight and other rows ticked.
      dropIds(removedIds);
      setIsConfirmingBulk(false);
    });
  };

  const clearSelectionAndRefocus = () => {
    clearSelection();
    focusAnchor();
  };

  const clearFiltersAndRefocus = () => {
    clearFilters();
    searchRef.current?.focus();
  };

  // Closing rolls nothing back — every add is already saved — so this is only a disclosure.
  const closeAddPanel = () => {
    setIsAddOpen(false);
    addButtonRef.current?.focus();
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-py-6 tw-text-sm tw-text-dark-grey">
        <Spinner /> {I18n.t('loading')}
      </div>
    );
  }

  if (error) {
    return (
      <Text className="tw-text-very-dark-grey tw-py-6" size="sm">
        {I18n.t('correlations_load_error')}
      </Text>
    );
  }

  return (
    // The wrapper isn't a widget — the handler restores Space activation and suppresses Enter-submit
    // for the controls inside it, so the rule's role/keyboard-handler pairing doesn't apply.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={rootRef}
      className="tw-border tw-border-solid tw-border-grey tw-rounded-lg tw-bg-white"
      onKeyDown={onKeyDown}
    >
      <BulkRemoveModal
        correlations={selectedVisible}
        isOpen={isConfirmingBulk}
        isRemoving={isRemovingBulk}
        onCancel={() => setIsConfirmingBulk(false)}
        onConfirm={confirmBulkRemove}
      />

      <SelectionAnnouncer count={selectedVisible.length} />

      <FilterBar
        categoryOptions={categoryOptions}
        filters={filters}
        gradeOptions={gradeOptions}
        searchId={searchId}
        searchRef={searchRef}
        showGrade={showGrade}
        showState={showState}
        stateOptions={stateOptions}
        onFilterChange={setFilter}
      />

      <div className={cn('tw-overflow-y-auto', MAX_HEIGHT[variant])}>
        <Table className="tw-w-full tw-table-fixed tw-border-collapse">
          <TagManagerTableHeader
            allSelected={allSelected}
            hasSelectableRows={selectable.length > 0}
            selectAllRef={selectAllRef}
            onToggleSelectAll={toggleSelectAll}
          />
          <TableBody>
            {selectedVisible.length > 0 && (
              <BulkActionBar
                count={selectedVisible.length}
                disabled={isRemovingBulk || isRestoringBulk || isRowMutating}
                onClear={clearSelectionAndRefocus}
                onRemove={() => setIsConfirmingBulk(true)}
              />
            )}
            {visible.map((correlation, slot) => (
              <CorrelationRow
                key={correlation.id}
                correlation={correlation}
                isMutatingBatch={isRemovingBulk || isRestoringBulk}
                isSelected={selectedIds.has(correlation.id)}
                modelId={modelId}
                modelType={modelType}
                slot={slot}
                onRemove={removeCorrelationAction}
                onToggleSelect={toggleSelect}
                onUnhide={unHideCorrelationAction}
              />
            ))}
            {filtered.length > ROW_CAP && (
              <TableRow>
                <TableCell className="tw-text-center" colSpan={COLUMN_COUNT}>
                  <LinkButton
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-tci-purple"
                    onClick={() => setShowAll(!showAll)}
                  >
                    <Text size="sm" tag="span" weight="semibold">
                      {showAll ? I18n.t('show_less') : I18n.t('show_more_remaining', { count: remaining })}
                    </Text>
                    <Icon aria-hidden icon={showAll ? 'ChevronUp' : 'ChevronDown'} size={16} />
                  </LinkButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {filtered.length === 0 && (isFiltered ? (
          <EmptyState
            action={(
              <Button outlineStyle small onClick={clearFiltersAndRefocus}>{I18n.t('clear_filters')}</Button>
            )}
            title={I18n.t('no_standards_match_your_filters')}
          />
        ) : (
          <EmptyState body={I18n.t('no_correlations_yet_body')} title={I18n.t('no_correlations_yet')} />
        ))}
      </div>

      <TagManagerTableFooter
        addButtonRef={addButtonRef}
        categoryCount={categoryCount}
        hiddenCount={hiddenCount}
        isAddOpen={isAddOpen}
        panelId={addPanelId}
        visibleCount={selectable.length}
        onToggleAdd={() => (isAddOpen ? closeAddPanel() : setIsAddOpen(true))}
      />

      {isAddOpen && (
        <AddCorrelationPanel
          categories={categories}
          initialCategory={filters.category}
          modelId={modelId}
          modelType={modelType}
          panelId={addPanelId}
          programId={programId}
          variant={variant}
          onClose={closeAddPanel}
        />
      )}
    </div>
  );
};

export default TagManagerTable;
